import { useCallback, useEffect, useRef, useState } from "react";
import { unwrapArray, unwrapObject } from "@/lib/api";

export const DEFAULT_PAGE_SIZE = 30;

export interface PageResult<T> {
  rows: T[];
  total: number;
  page: number;
  page_size: number;
}

/** Reads `{ rows, total, page, page_size }` from a list response. */
export function unwrapPage<T>(res: any, fallbackSize = DEFAULT_PAGE_SIZE): PageResult<T> {
  const data = unwrapObject<any>(res, null);
  if (data && Array.isArray(data.rows)) {
    return {
      rows: data.rows,
      total: Number(data.total) || 0,
      page: Number(data.page) || 1,
      page_size: Number(data.page_size) || fallbackSize,
    };
  }
  const rows = unwrapArray<T>(res);
  return { rows, total: rows.length, page: 1, page_size: rows.length || fallbackSize };
}

export function hasMorePage(page: PageResult<unknown>) {
  return page.page * page.page_size < page.total;
}

/**
 * Numbered pages for a table. Changing `deps` (filters, search) goes back to page 1.
 * `load` should include the page number in its request and return unwrapPage(...).
 */
export function usePagedList<T>(options: {
  deps: readonly unknown[];
  debounceMs?: number;
  load: (page: number) => Promise<PageResult<T>>;
}) {
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const loadRef = useRef(options.load);
  loadRef.current = options.load;
  const pageRef = useRef(page);
  pageRef.current = page;
  const depsKey = JSON.stringify(options.deps);
  const prevDeps = useRef(depsKey);
  const debounceMs = options.debounceMs ?? 0;

  const run = useCallback(async (pageToLoad: number) => {
    setLoading(true);
    try {
      const res = await loadRef.current(pageToLoad);
      setRows(res.rows);
      setTotal(res.total);
      setPageSize(res.page_size || DEFAULT_PAGE_SIZE);
      return res;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    let pageToLoad = page;
    if (prevDeps.current !== depsKey) {
      prevDeps.current = depsKey;
      if (page !== 1) {
        setPage(1);
        return;
      }
      pageToLoad = 1;
    }
    const timer = setTimeout(() => {
      run(pageToLoad).catch(() => {
        if (!cancelled) {
          setRows([]);
          setTotal(0);
        }
      });
    }, debounceMs);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [depsKey, page, debounceMs, run]);

  const reload = useCallback(() => run(pageRef.current), [run]);

  return { page, setPage, rows, total, pageSize, loading, reload };
}
